import { Signal } from '@preact/signals-react'
import { BlockEngine } from 'shared/express-interface/blocks-core/BlockEngine'
import { ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'

export interface IPluginConnector {
    pluginId: string
    id: string
    getData: () => Record<string, Signal<unknown> | null>
}

export type PluginDataKeyMap = {
    Plagiarism: 'text'
    'AI Detection': 'text'
    'Live Feedback': 'imageUrl' | string
    'Grammar Check': 'text'
    'sapling-grammar': 'text'
    Readability: 'text'
    'Sentiment Analysis': 'text'
    'Soft Rules': 'text'
    'Math Type': 'text'
    'Summarisation Quality': 'text'
    'AI Assistance': 'text'
    'Custom Validation': 'text'
}

export type BlockConnectionConfigType<
    TPluginId extends keyof PluginDataKeyMap = keyof PluginDataKeyMap,
> = {
    blockType: string
    blockId: string
    blockProperty: string
    pluginDataKey: TPluginId extends keyof PluginDataKeyMap ? PluginDataKeyMap[TPluginId] : string
}

export type ConnectorConfigType<TPluginId extends keyof PluginDataKeyMap = keyof PluginDataKeyMap> =
    {
        id: string
        pluginId: TPluginId
        blockConnectionConfigs: BlockConnectionConfigType<TPluginId>[]
    }

export function createConnectorConfig<TPluginId extends keyof PluginDataKeyMap>(
    config: ConnectorConfigType<TPluginId>
): ConnectorConfigType<TPluginId> {
    return config
}

export class PluginConnector implements IPluginConnector {
    pluginId: string
    id: string
    blockConnectionConfigs: BlockConnectionConfigType[]
    private blockEngine: BlockEngine
    constructor(
        { id, pluginId, blockConnectionConfigs }: ConnectorConfigType,
        blockEngine: BlockEngine
    ) {
        this.pluginId = pluginId
        this.id = id
        this.blockConnectionConfigs = blockConnectionConfigs
        this.blockEngine = blockEngine
    }

    getData() {
        const data: Record<string, Signal<unknown> | null> = {}
        for (const config of this.blockConnectionConfigs) {
            const block = this.blockEngine.getBlock(config.blockId)
            if (block) {
                const blockData = block.getState(config.blockProperty)
                if (blockData) {
                    data[config.pluginDataKey] = blockData
                }
            }
        }
        return data
    }

    getConnectedBlocks(): ITaskBlock[] {
        return this.blockConnectionConfigs
            .map(config => this.blockEngine.getBlock(config.blockId))
            .filter(block => block !== undefined)
    }
}
